import { Children, cloneElement, isValidElement, type ReactNode } from "react"
import { localeHref, type Locale } from "./routing"

export type Dictionary = Record<string, string>
export type Translator = (text: string) => string
const fixedProps = new Set(["className", "id", "key", "ref", "name", "type", "value", "defaultValue", "src", "srcSet", "sizes", "style", "htmlFor", "method", "autoComplete", "inputMode", "role", "target", "rel", "variant", "size", "data-icon", "data-slot", "aria-controls", "aria-describedby", "aria-labelledby", "aria-current"])
const stableDataKeys = new Set(["slug", "id", "key", "cutout", "category", "kind", "type", "date", "time", "returnDate", "returnTime", "phone", "email", "image", "hero", "video", "poster", "signature", "source", "sourcePage", "publishedTime", "modifiedTime", "@id", "@type", "@context", "addressRegion", "addressCountry", "postalCode", "streetAddress", "latitude", "longitude", "currency", "reference"])
const linkKeys = new Set(["href", "path", "serviceHref"])
const humanProps = new Set(["title", "label", "description", "intro", "eyebrow", "copy", "note", "subject", "text", "heading", "alt", "placeholder", "aria-label", "aria-description", "aria-valuetext"])
const technicalKeys = new Set(["document", "step", "service", "vehicle", "mode", "format", "state", "status", "active", "orientation", "variant", "size", "as", "initialCategory", "initialQuery", "field", "pattern", "value", "defaultValue", "token", "layout", "route", "timeZone", "lang", "hrefLang"])
const escapeRegex = (text: string) => Array.from(text, character => "\\^$.*+?()[]{}|".includes(character) ? `\\${character}` : character).join("")

export function createTranslator(dictionary: Dictionary, locale: Locale): Translator {
  void locale
  const patterns = Object.entries(dictionary)
    .filter(([key, value]) => key !== value && /\{\d+\}/.test(key) && /[A-Za-z]/.test(key.replace(/\{\d+\}/g, "")))
    .sort(([left], [right]) => right.replace(/\{\d+\}/g, "").length - left.replace(/\{\d+\}/g, "").length)
    .map(([key, value]) => ({ expression: new RegExp(`^${key.split(/\{\d+\}/).map(escapeRegex).join("(.+?)")}$`), value }))
  const memo = new Map<string, string>()
  const translate = (text: string): string => {
    if (!text || !/[A-Za-z]/.test(text)) return text
    const cached = memo.get(text)
    if (cached !== undefined) return cached
    const normalized = text.trim().replace(/\s+/g, " ")
    let result = dictionary[normalized]
    if (result === undefined && normalized.endsWith(" | Danaro Limo")) result = `${translate(normalized.slice(0, -14))} | Danaro Limo`
    if (result === undefined) {
      for (const pattern of patterns) {
        const match = normalized.match(pattern.expression)
        if (match) { result = pattern.value.replace(/\{(\d+)\}/g, (_, index: string) => translate(match[Number(index) + 1] ?? "")); break }
      }
    }
    const final = result === undefined ? text : `${text.match(/^\s*/)?.[0] ?? ""}${result}${text.match(/\s*$/)?.[0] ?? ""}`
    memo.set(text, final)
    return final
  }
  return translate
}

export function localizeData<T>(data: T, t: Translator, locale: Locale, key = ""): T {
  if (typeof data === "string") {
    if (linkKeys.has(key)) return localeHref(data, locale) as T
    if (stableDataKeys.has(key) || technicalKeys.has(key)) return data
    return t(data) as T
  }
  if (Array.isArray(data)) return data.map(item => localizeData(item, t, locale, key)) as T
  if (data && typeof data === "object" && Object.getPrototypeOf(data) === Object.prototype && !isValidElement(data)) {
    return Object.fromEntries(Object.entries(data).map(([childKey, value]) => [childKey, childKey === "name" && "label" in data ? value : localizeData(value, t, locale, childKey)])) as T
  }
  return data
}

export function localizeTree(node: ReactNode, t: Translator, locale: Locale,
  images: Record<string,{src:string;altEn:string;altEs:string;decorative:boolean;fit?:"preserve"|"contain"|"cover";focusX?:number|null;focusY?:number|null}> = {}): ReactNode {
  if (typeof node === "string") return t(node)
  if (Array.isArray(node)) return Children.map(node, child => localizeTree(child, t, locale, images))
  if (!isValidElement<Record<string, unknown>>(node)) return node
  if (node.props.translate === "no" || node.type === "script" || node.type === "style") return node
  const props: Record<string, unknown> = {}
  const originalImage = typeof node.props.src === "string" ? images[node.props.src] : undefined
  for (const [key, value] of Object.entries(node.props)) {
    // CMS documents already own their language and canonical template path.
    // Localizing their data again changes /blog/... into /es/blog/... and loses
    // the template contract, rendering every core section a second time.
    if (key === "content" && value && typeof value === "object" && "language" in value && "path" in value && "sections" in value) { props[key] = value; continue }
    if (key === "src" && originalImage) { props[key] = originalImage.src; continue }
    if (key === "alt" && originalImage) {
      props[key] = originalImage.decorative ? "" : locale === "es" ? originalImage.altEs : originalImage.altEn; continue
    }
    if (fixedProps.has(key) || key.startsWith("data-") || key.startsWith("on")) { props[key] = value; continue }
    if (key === "children" || isValidElement(value)) props[key] = localizeTree(value as ReactNode, t, locale, images)
    else if (key === "href" && typeof value === "string") props[key] = localeHref(value, locale)
    else if (typeof value === "string") props[key] = humanProps.has(key) || /(?:Label|Title|Text|Description|Heading)$/.test(key) ? t(value) : value
    else props[key] = localizeData(value, t, locale, key)
  }
  if (originalImage) {
    if (originalImage.src !== node.props.src) delete props.srcSet
    if (originalImage.fit && originalImage.fit !== "preserve") props.style = { ...(props.style && typeof props.style === "object" ? props.style : {}), objectFit: originalImage.fit, objectPosition: `${originalImage.focusX ?? 50}% ${originalImage.focusY ?? 50}%` }
  }
  return cloneElement(node, props)
}
